import React, { FunctionComponent } from 'react';
import cx from 'classnames';
import Image from 'next/image';
import Link from 'next/link';
import { ArticlePreviewProps, Preview } from './ArticlePreview.types';
import prepareImageUrl from '../../utils/prepareAssetUrl';
import styles from './ArticlePreview.module.scss';
import ImagePlaceholder from '../ImagePlaceholder';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import Button from '../Button';
import { ButtonType } from '../Button/Button.types';
import ContentTypeLabel, { ContentTypeEnum } from '../ContentTypeLabel';
import { useTranslations } from '../../hooks/useTranslations';
import type { EntrySkeletonType } from 'contentful';
import type { ArticleFeaturedImage } from '../../types/common/Article.types';
import type { MarkdownArticleFeaturedImage } from '../../lib/articles';

const isContentfulFeaturedImage = (
    featuredImage: ArticlePreviewProps['featuredImage'],
): featuredImage is EntrySkeletonType<ArticleFeaturedImage> => Boolean(
    featuredImage && 'fields' in featuredImage && featuredImage.fields.image,
);

const isMarkdownFeaturedImage = (
    featuredImage: ArticlePreviewProps['featuredImage'],
): featuredImage is MarkdownArticleFeaturedImage => Boolean(
    featuredImage && 'src' in featuredImage && featuredImage.src,
);

const ArticlePreview: FunctionComponent<ArticlePreviewProps> = ({
    title, createdDate, featuredImage, excerpt, slug, className, preview, externalSource, showContentType,
}) => {
    const imageWidth = preview === Preview.VERTICAL ? 352 : 544;
    const imageHeight = preview === Preview.VERTICAL ? 216 : 289;
    const contentfulImage = isContentfulFeaturedImage(featuredImage) ? featuredImage : null;
    const markdownImage = isMarkdownFeaturedImage(featuredImage) ? featuredImage : null;
    const contentfulImageUrl = contentfulImage?.fields.image.fields.file.url;
    const imageSrc = typeof contentfulImageUrl === 'string' ? contentfulImageUrl : markdownImage?.src;
    const imageAlt = contentfulImage
        ? `${contentfulImage.fields.title} by ${contentfulImage.fields.author}`
        : markdownImage?.alt || markdownImage?.title || title;

    const { t } = useTranslations();
    
    return (
        <article className={cx(styles.articlePreview, className)}>
            <div className={styles[preview]}>
                {
                    externalSource ? (
                        <a href={externalSource} target="_blank" rel="noopener noreferrer">
                            {
                                imageSrc ?
                                    (
                                        <div className={styles.imageBox}>
                                            <Image
                                                src={prepareImageUrl(imageSrc)}
                                                width={imageWidth}
                                                height={imageHeight}
                                                className={styles.image}
                                                alt={imageAlt}
                                            />
                                            {externalSource &&  <div className={styles.external}>External</div>}
                                        </div>
                                    ) :
                                    <ImagePlaceholder width="352px" height="216px" />
                            }
                        </a>
                    ) : (
                        <Link href={`/blog/${slug}`}>
                            {
                                imageSrc ?
                                    (
                                        <div className={styles.imageBox}>
                                            <Image
                                                src={prepareImageUrl(imageSrc)}
                                                width={imageWidth}
                                                height={imageHeight}
                                                className={styles.image}
                                                alt={imageAlt}
                                            />
                                            {externalSource && <div className={styles.external}>External</div>}
                                        </div>
                                    ) :
                                    <ImagePlaceholder width="352px" height="216px" />
                            }
                        </Link>
                    )
                }
                
                <div className={styles.content}>
                    <div>
                        {showContentType && <ContentTypeLabel contentType={ContentTypeEnum.ARTICLE} />}
                        <div className={styles.date}>{createdDate}</div>
                        {
                            externalSource ? (
                                <a href={externalSource} target="_blank" rel="noopener noreferrer">
                                    <h3 className={styles.title}>{title}</h3>
                                </a>
                            ) : (
                                <Link href={externalSource || `/blog/${slug}`} passHref={!!externalSource}>
                                    <h3 className={styles.title}>{title}</h3>
                                </Link>
                            )
                        }
                        {excerpt && documentToReactComponents(excerpt, {})}
                        </div>
                    {
                        preview === Preview.HORIZONTAL && (
                            <Button.L
                                isExternal={!!externalSource}
                                href={externalSource || `/blog/${slug}`}
                                passHref={!!externalSource}
                                pattern={ButtonType.PRIMARY}
                                label={t.ARTICLE.ACTIONS.READ_MORE}
                            />
                        )
                    }
                </div>
            </div>
            {
                preview === Preview.VERTICAL && (
                    <Button.L 
                        isExternal={!!externalSource}
                        href={externalSource || `/blog/${slug}`}
                        passHref={!!externalSource}
                        pattern={ButtonType.PRIMARY}
                        label={t.ARTICLE.ACTIONS.READ_MORE}
                    />
                )
            }
        </article>
    )
}

export default ArticlePreview;
